/**
 * Redesign copy engine (#947, doctrine rules 1 + 4 in
 * `threads/tech-app-redesign/SPEC.md`).
 *
 * The audit's finding, in one line: the field app shows the tech raw import
 * strings. A ServiceTitan job whose summary is "REPLACE SN: H94730" renders
 * that verbatim as the title, and a brand-new hire has no idea what it means
 * or what to do. Rule 1: **no raw import string is ever a title** — titles
 * COMPOSE from structured fields (job type + device count + customer + city),
 * and the office's original wording demotes to a labeled, collapsed note.
 *
 * Pure functions on purpose: this is the copy contract the whole redesign
 * leans on (home hero, job rows, upcoming, the job page header), so it is
 * unit-tested once here instead of re-derived per screen.
 */

/** The subset of a job row these helpers read. */
export interface TitleSource {
    title: string | null;
    job_type_name: string | null;
    trade_name?: string | null;
    trade_code?: string | null;
    customer_name?: string | null;
    site_name?: string | null;
    site_city?: string | null;
    /** Devices linked to the job, when the caller knows it. */
    device_count?: number | null;
}

/**
 * Import strings we must never show a tech. These are the shapes the audit
 * actually caught in production titles — ALL-CAPS action codes, bare serial
 * fragments, and vendor work-order numbers.
 */
const IMPORT_TITLE_PATTERNS: RegExp[] = [
    /\bSN[:#]?\s*[A-Z0-9-]{4,}/i, // "REPLACE SN: H94730"
    /\bS\d{7,}\b/, // ServiceTitan job ids
    /\b\d{8,}\b/, // bare WO / order numbers
    /^[A-Z0-9 ,.:#/-]{8,}$/, // shouty all-caps import blobs
];

/** True when a stored title is machine wording rather than something a tech
 *  should read. Exported for the job page's "original wording" collapse. */
export function looksLikeImportString(title: string | null | undefined): boolean {
    const t = (title ?? "").trim();
    if (!t) return false;
    return IMPORT_TITLE_PATTERNS.some((re) => re.test(t));
}

/** Plain trade word for a title, when the job type itself is missing. */
const TRADE_WORD: Record<string, string> = {
    backflow: "Backflow test",
    fire_ext: "Fire extinguisher check",
    underground_fire: "Underground fire line",
    hvac: "HVAC visit",
};

/**
 * Size brackets the OFFICE uses to price work, which mean nothing to the
 * person driving to the job.
 *
 * Job types are named for the price book: "Backflow Replacement — 2\" and
 * Smaller", "Backflow Repair — 2½\" and Larger". The bracket decides what the
 * customer is billed; the tech just needs to know it's a replacement. Showing
 * the bracket is the exact failure this redesign exists to kill — office
 * vocabulary on a tech's screen — and it survived the audit because the audit
 * looked at job TITLES and never at job TYPE names (#1059).
 *
 * Deliberately narrow: it only strips a trailing size-and-comparison clause,
 * so "Backflow Clean & Flush" and "HVAC Service Call" are untouched.
 */
const SIZE_BRACKET_SUFFIX =
    /\s*[—–-]\s*\d+\s*[½¼¾]?\s*["”″']?\s*(?:and|or)\s+(?:smaller|larger|below|above|under|over)\s*$/i;

/**
 * A job type as a tech should read it — the office's pricing bracket removed.
 *
 * Returns the name unchanged when there's no bracket, and never returns an
 * empty string: if a type were somehow nothing BUT a bracket, the original
 * wins, because a wrong-but-present label beats a blank one.
 */
export function plainJobTypeName(name: string | null | undefined): string {
    const raw = (name ?? "").trim();
    if (!raw) return "";
    const stripped = raw.replace(SIZE_BRACKET_SUFFIX, "").trim();
    return stripped || raw;
}

/**
 * The plain-words job title (SPEC H3/H4/J1).
 *
 * Priority: the job type is the truth ("Yearly Backflow Test"); a device count
 * makes it concrete ("— 2 devices"); the trade is the fallback when no type is
 * on file. A stored title is used ONLY when it reads like something a person
 * wrote — never when it looks like an import string.
 *
 * Always returns something a tech can act on: the last resort is "Service
 * visit", never a blank or a code.
 */
export function composeJobTitle(job: TitleSource): string {
    const devices = job.device_count ?? null;
    const deviceSuffix = devices != null && devices > 0 ? ` — ${devices} device${devices === 1 ? "" : "s"}` : "";

    const type = plainJobTypeName(job.job_type_name);
    if (type) return `${type}${deviceSuffix}`;

    const stored = (job.title ?? "").trim();
    if (stored && !looksLikeImportString(stored)) return `${stored}${deviceSuffix}`;

    const tradeWord = TRADE_WORD[(job.trade_code ?? "").toLowerCase()] ?? (job.trade_name ?? "").trim();
    if (tradeWord) return `${tradeWord}${deviceSuffix}`;

    return `Service visit${deviceSuffix}`;
}

/** Words too small to carry meaning — ignored when deciding whether the
 *  office's wording says anything the screen doesn't already say. */
const FILLER_WORDS = new Set(["a", "an", "and", "at", "for", "in", "of", "on", "the", "to", "with"]);

/** Comparable words in a phrase: lowercased, punctuation dropped, filler out. */
function meaningfulWords(text: string | null | undefined): string[] {
    return (text ?? "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, " ")
        .split(" ")
        .filter((w) => w && !FILLER_WORDS.has(w));
}

/**
 * The office's original wording, for the collapsed "Note from the office"
 * block (SPEC J3) — null when it says nothing the tech isn't already reading,
 * so the note never duplicates what's on the card (doctrine rule 2: one home
 * per fact).
 *
 * The test is "does this add a word we aren't already showing", not "is it a
 * prefix of the title". The prefix check alone let through the commonest shape
 * of all — a stored title that is just the job type plus the customer name,
 * e.g. "Backflow Test — Oak Ridge Dental" on a card already headed "Backflow
 * Test" for Oak Ridge Dental. That rendered as `Note from the office: "Backflow
 * Test — Oak Ridge Dental"`, which is noise on the screen a tech reads first
 * (#1059).
 *
 * An import string still always shows, however familiar its words look: it is
 * the office's actual reference and the tech may need to quote it back.
 */
export function originalOfficeWording(job: TitleSource): string | null {
    const stored = (job.title ?? "").trim();
    if (!stored) return null;
    if (looksLikeImportString(stored)) return stored;

    const alreadyShown = new Set(
        meaningfulWords(
            [composeJobTitle(job), job.customer_name, job.site_name, job.site_city].filter(Boolean).join(" "),
        ),
    );
    const words = meaningfulWords(stored);
    if (words.length === 0) return null;
    return words.every((w) => alreadyShown.has(w)) ? null : stored;
}

export interface GreetingJob {
    scheduled_start: string | null;
    site_city?: string | null;
}

/**
 * The day as ONE sentence (SPEC H1): how many jobs, when they start, and
 * where. Replaces a bare "My Jobs" heading that told a tech nothing.
 *
 * Deliberately honest about the two edge cases the audit caught: a day with
 * nothing scheduled says so plainly (and does not shame), and jobs with no
 * time yet are counted separately rather than folded into a start time that
 * doesn't exist.
 */
export function dayGreeting(
    jobs: GreetingJob[],
    opts: { firstName?: string | null; formatTime: (iso: string) => string },
): string {
    const hello = opts.firstName ? `${opts.firstName}, ` : "";
    const cap = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);

    if (jobs.length === 0) {
        return cap(`${hello}nothing is booked for you today.`);
    }

    const timed = jobs
        .filter((j) => !!j.scheduled_start)
        .sort((a, b) => (a.scheduled_start ?? "").localeCompare(b.scheduled_start ?? ""));
    const untimed = jobs.length - timed.length;

    const count = `${jobs.length} job${jobs.length === 1 ? "" : "s"}`;
    const cities = [...new Set(jobs.map((j) => (j.site_city ?? "").trim()).filter(Boolean))];
    const where =
        cities.length === 0
            ? ""
            : cities.length === 1
              ? ` in ${cities[0]}`
              : cities.length === 2
                ? ` in ${cities[0]} and ${cities[1]}`
                : ` across ${cities.length} towns`;

    if (timed.length === 0) {
        return cap(`${hello}you have ${count}${where} — no times set yet.`);
    }

    const first = opts.formatTime(timed[0]!.scheduled_start!);
    const tail = untimed > 0 ? ` (${untimed} with no time yet)` : "";
    return cap(`${hello}you have ${count}${where}, starting at ${first}.${tail}`);
}

/**
 * Job states that are OFF the tech's hands (SPEC J11, doctrine rule 3:
 * "done means done"). The audit found terminal jobs still showing live
 * buttons and office-owned tasks, so a tech couldn't tell whether anything
 * was still expected of them.
 */
const DONE_STATES = new Set(["office_review", "completed", "invoiced", "closed"]);

export function isJobDoneForTech(status: string | null | undefined): boolean {
    return DONE_STATES.has((status ?? "").trim());
}

export interface DoneReceipt {
    /** Banner headline — what happened, in the tech's words. */
    headline: string;
    /** What the OFFICE does next, so the status isn't a mystery (J11/W6). */
    officeNext: string;
}

/**
 * The "all wrapped up" receipt for a finished job. Each state says plainly
 * what the tech did and what happens now — a tech meeting `office_review`
 * for the first time should not have to ask what it means.
 */
export function doneReceipt(status: string | null | undefined): DoneReceipt | null {
    switch ((status ?? "").trim()) {
        case "office_review":
            return {
                headline: "All wrapped up — sent to the office.",
                officeNext:
                    "The office checks your paperwork and files the report. They'll message you only if something's missing.",
            };
        case "completed":
            return {
                headline: "All wrapped up.",
                officeNext: "The office has your paperwork and is filing the report with the city.",
            };
        case "invoiced":
            return {
                headline: "All wrapped up — the customer has been billed.",
                officeNext: "Nothing else is needed from you on this one.",
            };
        case "closed":
            return {
                headline: "All wrapped up and closed out.",
                officeNext: "This job is finished. It stays here so you can look back at what you did.",
            };
        default:
            return null;
    }
}

/**
 * Plain-words meaning for every job status a tech can meet (SPEC J12,
 * doctrine rule 4: "every number explained IN PLACE" — the explanation
 * lives on the screen, not in a manual a green hire will never open).
 *
 * Written from the TECH's point of view: what it means for them, and whose
 * turn it is. Deliberately not the office's vocabulary — "office_review"
 * is a workflow state name, not an answer to "do I still have to do
 * something here?".
 */
const STATUS_MEANING: Record<string, string> = {
    draft: "The office is still setting this one up.",
    awaiting_scheduling: "Not on the calendar yet — the office will pick a day.",
    unqueued: "Taken off the schedule for now. Nothing to do.",
    scheduled: "Booked for you. Head there at the time shown.",
    en_route: "You said you're on the way.",
    arrived: "You're checked in on site.",
    in_progress: "You're working this one — finish the steps below.",
    office_review: "You sent it. The office is checking the paperwork.",
    cannot_complete: "You couldn't finish it, and you said why. The office takes it from here.",
    held: "On hold — the office is waiting on something (a part, an approval, the customer).",
    completed: "Finished. Nothing else is needed from you.",
    closed: "Finished and closed out.",
    cancelled: "Called off. Don't go.",
};

/** The plain-words meaning of a status, or null when we have nothing honest
 *  to say (better silent than a made-up gloss). */
export function statusMeaning(status: string | null | undefined): string | null {
    return STATUS_MEANING[(status ?? "").trim()] ?? null;
}

/* ── J3: the scope, in plain words ──────────────────────────────────────── */

export interface ScopePlainWords {
    /** "Today: test 2 backflows." — what this visit IS. */
    doing: string;
    /** The boundary. Null only when we genuinely can't tell what's out of scope. */
    notDoing: string | null;
    /** The office's own scope text, for the labeled collapse. Null when there
     *  is none, or when it says the same thing the plain line already does. */
    officeWording: string | null;
    /** Set when the office's words and the actual work disagree (J3's
     *  "conflict called out"). */
    conflict: string | null;
}

/** Words in an office scope that promise repair work. */
const REPAIR_WORDS = /\b(repair|rebuild|rekit|re-kit|replace parts|fix)\b/i;

/**
 * J3 — what today's visit actually is, in the tech's words.
 *
 * The audit's finding: the office's scope text is pasted in verbatim and often
 * describes a whole relationship ("annual testing and repair as needed"),
 * while TODAY's job is one test. A green tech reads that as authorisation to
 * start pulling a valve apart.
 *
 * So this states the visit plainly, states the boundary explicitly, demotes
 * the office's wording to a labeled note, and — the part that matters —
 * **calls out a disagreement instead of silently picking a side.**
 */
export function scopePlainWords(opts: {
    scopeOfWork?: string | null;
    /** `required_action` per device on this job. */
    actions: Array<string | null | undefined>;
    deviceCount: number;
}): ScopePlainWords {
    const acts = opts.actions.map((a) => (a ?? "").trim().toLowerCase()).filter(Boolean);
    const replacing = acts.filter((a) => a === "replace").length;
    const repairing = acts.filter((a) => a === "repair").length;
    const n = opts.deviceCount;

    const parts: string[] = [];
    const testing = n - replacing - repairing;
    if (testing > 0) parts.push(`test ${testing} backflow${testing === 1 ? "" : "s"}`);
    if (replacing > 0) parts.push(`replace ${replacing} device${replacing === 1 ? "" : "s"}`);
    if (repairing > 0) parts.push(`repair ${repairing} device${repairing === 1 ? "" : "s"}`);

    const doing =
        parts.length === 0
            ? n > 0
                ? `Today: ${n} device${n === 1 ? "" : "s"} to work.`
                : "Today's work is on the job page below."
            : `Today: ${parts.join(", ")}.`;

    // The boundary. Only claimed when we KNOW no repair is scheduled — saying
    // "you are not repairing" on a repair job would be worse than silence.
    // Never on a replace job either: "you are NOT repairing valves today" is
    // test-job wording (2026-10-22, the 7-Eleven replace).
    const notDoing =
        repairing === 0 && replacing === 0 && acts.length > 0
            ? "You are NOT repairing valves today. If one fails, take your photos and finish — the office prices the repair and books it."
            : null;

    const office = (opts.scopeOfWork ?? "").trim();
    const conflict =
        office && REPAIR_WORDS.test(office) && repairing === 0 && replacing === 0
            ? "The office's note below mentions repairs, but no repair is scheduled on this visit. Test it, record the failure, and let the office sort the repair — don't start one on your own."
            : null;

    return { doing, notDoing, officeWording: office || null, conflict };
}

/* ── J5: one location, and which one is right ───────────────────────────── */

export interface LocationTruthCard {
    title: string;
    body: string;
    /** The two answers, in the order they should be offered. */
    choices: Array<{ key: "device" | "work_order"; label: string; value: string }>;
    /** What tapping one actually does — never left implicit. */
    footnote: string;
}

/**
 * J5 — the ONE amber card for a device whose two recorded locations disagree.
 *
 * The work order says one thing, the device record says another, and today the
 * tech gets neither: the field page shows the device's location and says
 * nothing about the other. So a tech walks to the wrong side of the building
 * on the office's word, or "corrects" a record that was right.
 *
 * The card names both answers and asks which one is true. The tech's tap is
 * the settlement — it writes the device record and stamps the acknowledgement,
 * so the card is gone for good rather than reappearing next year.
 */
export function locationTruthCard(opts: {
    deviceLabel: string;
    deviceLocation?: string | null;
    workOrderLocation?: string | null;
}): LocationTruthCard | null {
    const device = (opts.deviceLocation ?? "").trim();
    const wo = (opts.workOrderLocation ?? "").trim();
    // Nothing to settle unless there are genuinely two different answers.
    if (!wo || device.toLowerCase() === wo.toLowerCase()) return null;

    const choices: LocationTruthCard["choices"] = [];
    if (device) choices.push({ key: "device", label: `It's ${device}`, value: device });
    choices.push({ key: "work_order", label: `It's ${wo}`, value: wo });

    return {
        title: "Two different locations on file",
        body: device
            ? `Our record says ${opts.deviceLabel} is ${device}. The work order says ${wo}. You're the one standing there — which is right?`
            : `The work order says ${opts.deviceLabel} is ${wo}. We have nothing on file. Is that where it is?`,
        choices,
        footnote: "Your answer updates the device record for good — nobody gets sent to the wrong spot next year.",
    };
}

/* ── J9: photos a person can tell apart ─────────────────────────────────── */

/** The slot names the field app files photos under, in tech words. */
const PHOTO_TAG_LABEL: Record<string, string> = {
    tag: "Tag photo",
    device: "Device photo",
    area: "Area photo",
    fire_main_tag: "Fire main tag",
    fire_bypass_tag: "Fire bypass tag",
    meter_number: "Meter number",
    meter_reading: "Meter reading",
    condition: "Condition photo",
    flush: "Flush photo",
    before: "Before photo",
    after: "After photo",
    signature: "Signature",
    customer_signature: "Customer signature",
};

/** A filename with nothing in it for a human — uuids, camera dumps, blobs. */
export function isMachineFileName(name: string | null | undefined): boolean {
    const n = (name ?? "").trim();
    if (!n) return true;
    const stem = n.replace(/\.[a-z0-9]+$/i, "");
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(stem)) return true;
    if (/^(image|photo|img|dsc|pxl|screenshot)[-_ ]?\d*$/i.test(stem)) return true;
    if (/^[0-9a-f]{16,}$/i.test(stem)) return true;
    if (/^\d{8,}$/.test(stem)) return true;
    return false;
}

/**
 * J9 — what a photo row should SAY.
 *
 * A uuid is not a name. The field app already files photos under semantic
 * slots and against a device, so the information to name them has been there
 * all along — it just never reached the row. A human-written filename is
 * always kept; only machine names are replaced.
 */
export function photoDisplayName(
    file: { fileName?: string | null; photoTag?: string | null; assetSerial?: string | null },
    opts: { index?: number } = {},
): string {
    const rawTag = (file.photoTag ?? "").trim();
    const tag = rawTag.toLowerCase();
    const named = PHOTO_TAG_LABEL[tag];
    const serial = (file.assetSerial ?? "").trim();

    if (named) return serial ? `${named} — ${serial}` : named;
    // A photo_tag isn't always a slug — the office writes real sentences into
    // it ("Backflow area photo — front yard"). Anything a person clearly
    // typed wins outright, exactly like a human-written filename does.
    if (rawTag && !/^[a-z0-9_]+$/i.test(rawTag)) return rawTag;
    if (!isMachineFileName(file.fileName)) return (file.fileName ?? "").trim();
    if (serial) return `Photo of ${serial}`;
    return opts.index != null ? `Photo ${opts.index + 1}` : "Photo";
}
